import type { Table } from "@tanstack/react-table";
import { ensureIsString } from "common/utils/assertOfType";
import { useMemo } from "react";
import Link from "ui/components/Link";
import LoadingDots from "ui/components/LoadingDots";
import cx from "ui/cx";
import Select from "ui/forms/Select";
import IconTriangle from "ui/icons/IconTriangle";
import { PAGE_SIZES } from "./constants";
import styles from "./DataTable.module.scss";
import type { ClientPaginationState } from "./types";

interface PaginationProps<T> {
  pagination: ClientPaginationState;
  setPagination: (pagination: ClientPaginationState) => void;
  table: Table<T>;
  tableWidth: number | undefined;
  totalCount?: number;
  canMultiSelect: boolean;
  paginationMethod?: "offset" | "cursor";
  stickyPagination?: boolean;
  pageSizes?: number[];
  isLoading?: boolean;
}

/**
 * Determines whether to show pagination controls for cursor-based pagination.
 *
 * @param externalPagination - The external pagination state.
 * @returns boolean
 */
export function shouldShowCursorPagination(externalPagination: ClientPaginationState | null | undefined): boolean {
  if (!externalPagination) {
    return false;
  }

  const pageInfo = externalPagination.pageInfoByIndex?.[externalPagination.pageIndex];

  return !!pageInfo?.hasPreviousPage || !!pageInfo?.hasNextPage || !!externalPagination.pageIndex;
}

export function Pagination<T>({
  pagination,
  setPagination,
  table,
  tableWidth,
  totalCount,
  canMultiSelect,
  paginationMethod = "offset",
  stickyPagination = false,
  pageSizes = PAGE_SIZES,
  isLoading = false,
}: PaginationProps<T>) {
  const PAGE_SIZE_OPTIONS = useMemo(
    () =>
      pageSizes.map((o) => ({
        value: String(o),
        label: String(o),
      })),
    [pageSizes],
  );
  const { pageIndex, pageSize, pageInfoByIndex } = pagination;
  const rowCount = totalCount ?? table.getFilteredRowModel().rows.length;
  const pageCount = totalCount ? Math.ceil(totalCount / pageSize) : table.getPageCount();
  const pageOptions = useMemo(() => {
    const pages: Array<{ value: string; label: string }> = [];
    for (let i = 1; i <= pageCount; i++) {
      pages.push({ value: String(i), label: String(i) });
    }
    return pages;
  }, [pageCount]);

  const firstItem = pageSize * pageIndex + 1;
  const lastItem = Math.min(pageSize * (pageIndex + 1), rowCount);

  const narrow = tableWidth !== undefined && tableWidth < 500;
  return (
    <div className={cx({ styles, rowWrapper: true, sticky: stickyPagination })}>
      {canMultiSelect && <div className={cx(styles.dummyCell, styles.multiSelectCell)} />}
      <div
        className={cx(
          styles.row,
          styles.pagination,
          narrow && styles.narrow,
          tableWidth !== undefined && tableWidth < 250 && styles.tiny,
        )}
      >
        {isLoading ? (
          <div className={cx(styles.cell, styles.dynamicCell, styles.equalPadding)}>
            <div className={cx(styles.cellContents)}>
              Loading pagination data
              <LoadingDots />
            </div>
          </div>
        ) : (
          <>
            <div className={cx(styles.cell, styles.dynamicCell, styles.equalPadding)}>
              <div className={cx(styles.cellContents, styles.withSelect)}>Items per page:</div>
              <Select
                className={styles.selectPageSize}
                selectSize="medium"
                value={pageSize}
                options={PAGE_SIZE_OPTIONS}
                onChange={(value) =>
                  setPagination({ pageIndex: 0, pageSize: parseInt(ensureIsString(value)), pageInfoByIndex: {} })
                }
              />
            </div>
            {paginationMethod === "offset" && (
              <>
                <div className={cx(styles.cell, styles.equalPadding)}>
                  <div className={styles.cellContents}>
                    {firstItem !== lastItem ? `${firstItem} - ${lastItem}` : firstItem} of {rowCount} items
                  </div>
                </div>
                <div className={cx(styles.cell, styles.dynamicCell, styles.equalPadding)}>
                  <Select
                    className={styles.selectPageSize}
                    selectSize="medium"
                    value={pageIndex + 1}
                    options={pageOptions}
                    onChange={(value) =>
                      setPagination({ pageIndex: parseInt(ensureIsString(value)) - 1, pageSize, pageInfoByIndex })
                    }
                  />
                  <div className={cx(styles.cellContents, styles.withSelect)}>of {pageCount} pages</div>
                </div>
              </>
            )}
            <div className={cx(styles.cell, styles.equalPadding, styles.arrowCell, styles.arrowCell)}>
              <Link
                className={styles.arrow}
                disabled={pageIndex === 0}
                disabledCx={styles.disabledArrow}
                onClick={() => setPagination({ pageIndex: pageIndex - 1, pageSize, pageInfoByIndex })}
              >
                <IconTriangle rotate={-90} size={10} />
              </Link>
            </div>
            <div className={cx(styles.cell, styles.equalPadding, styles.arrowCell, styles.arrowCell)}>
              <Link
                className={styles.arrow}
                disabled={
                  paginationMethod === "offset"
                    ? pageIndex === pageCount - 1
                    : pageInfoByIndex?.[pageIndex]?.hasNextPage !== true
                }
                disabledCx={styles.disabledArrow}
                onClick={() => setPagination({ pageIndex: pageIndex + 1, pageSize, pageInfoByIndex })}
              >
                <IconTriangle rotate={90} size={10} />
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
